import { PIPELINE_STAGES } from '../pipeline/registry';
import { isStageEffectivelyEnabled } from '../pipeline/settings';
import { getApiKey, getOptimizationSettings, getContextTreesSettings, saveContextTreesSettings } from '../db/settings-repo';
import { getAllFiles } from '../db/files-repo';
import { getGitHubSource } from '../db/github-repo';
import { getCumulativeBenchmarkStats } from '../db/benchmarks-repo';
import { getStoredSession } from '../account/session';
import { contentHash } from '../nlp/content-hash';
import { icon, type IconName } from './icons';
import { createSwitch } from './switch';
import { createThemeToggle } from './theme';
import { onViewChange, setView, isNarrow, type ViewId } from './view';

export type NavItemId = 'pipeline' | 'benchmarks' | 'account';

export interface NavRailCallbacks {
  /** Desktop "Account" — the rail has no account surface of its own, so the host opens the modal. */
  onOpenAccount: () => void;
}

interface NavItemSpec {
  id: NavItemId;
  label: string;
  glyph: IconName;
  /** Which single-pane mobile view this item maps to. */
  view: ViewId;
  /** Element scrolled into view (and briefly highlighted) on the desktop shell. */
  target?: string;
}

/**
 * Chat/Files deliberately absent: on desktop every pane is on screen at once (these were only
 * scroll-to shortcuts), and narrow layouts navigate with `mobile-tabbar.ts`'s own separate tab list,
 * which still has both. Removing them here costs no reachable surface.
 */
const NAV_ITEMS: NavItemSpec[] = [
  { id: 'pipeline', label: 'Pipeline', glyph: 'pipeline', view: 'view-optimize', target: '#pipeline-statement' },
  { id: 'benchmarks', label: 'Benchmarks', glyph: 'benchmarks', view: 'view-optimize', target: '#benchmarks-panel' },
  { id: 'account', label: 'Account', glyph: 'account', view: 'view-account' },
];

/**
 * Left rail: brand, primary navigation with live counts, the current workspace card, and a footer
 * showing account/key custody plus the theme toggle. The file tree itself is *not* rendered here —
 * `#file-explorer` sits between this element and the footer and stays owned by FileExplorer.
 *
 * Every number shown comes from the same stores the rest of the app reads; `refresh()` re-reads
 * them, and the host calls it whenever something that feeds a badge changes.
 */
export class NavRail {
  private root: HTMLElement;
  private footerRoot: HTMLElement;
  private callbacks: NavRailCallbacks;
  private buttons = new Map<NavItemId, HTMLButtonElement>();
  private badges = new Map<NavItemId, HTMLElement>();
  private workspaceEl!: HTMLElement;
  private footerBodyEl!: HTMLElement;
  private projectModeInput!: HTMLInputElement;
  private active: NavItemId = 'pipeline';

  constructor(container: HTMLElement, footer: HTMLElement, callbacks: NavRailCallbacks) {
    this.root = container;
    this.footerRoot = footer;
    this.callbacks = callbacks;
    this.render();
    this.renderFooter();
    onViewChange((view) => this.syncActiveToView(view));
    void this.refresh();
  }

  private render() {
    this.root.innerHTML = '';

    const brand = document.createElement('div');
    brand.className = 'brand';
    const mark = document.createElement('img');
    mark.className = 'brand-mark';
    mark.src = '/icons/icon.svg';
    mark.alt = '';
    mark.decoding = 'async';
    brand.appendChild(mark);
    const brandText = document.createElement('div');
    brandText.className = 'brand-text';
    brandText.innerHTML = '<span class="wordmark">NAULON</span><span class="brand-tagline">token-lean chat</span>';
    brand.appendChild(brandText);
    this.root.appendChild(brand);

    const nav = document.createElement('nav');
    nav.className = 'nav-list';
    nav.setAttribute('aria-label', 'Primary');
    for (const item of NAV_ITEMS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nav-item';
      button.dataset.nav = item.id;
      button.innerHTML = `${icon(item.glyph)}<span class="nav-label">${item.label}</span><span class="nav-badge"></span>`;
      button.addEventListener('click', () => this.select(item));
      nav.appendChild(button);
      this.buttons.set(item.id, button);
      this.badges.set(item.id, button.querySelector('.nav-badge')!);
    }
    this.root.appendChild(nav);
    this.setActive('pipeline');

    this.root.appendChild(this.renderProjectMode());

    this.workspaceEl = document.createElement('div');
    this.workspaceEl.className = 'workspace-card';
    this.workspaceEl.innerHTML = '<div class="workspace-label">Workspace</div><div class="workspace-name">No project loaded</div>';
    this.root.appendChild(this.workspaceEl);
  }

  /**
   * Project Mode is the same `ContextTreesSettings.enabled` flag the optimization panel's
   * ContextTrees switch writes — two controls over one stored value. This one deliberately keeps no
   * cached settings object: it re-reads the stored record on every toggle and writes the whole thing
   * back with only `enabled` changed, so it can never clobber a review-mode the panel just saved.
   * Both surfaces re-read on render/refresh, which is what keeps them converged — there is no
   * cross-component subscription and none is needed.
   */
  private renderProjectMode(): HTMLElement {
    const section = document.createElement('div');
    section.className = 'project-mode-card';

    const row = document.createElement('div');
    row.className = 'project-mode-row';
    const label = document.createElement('span');
    label.className = 'project-mode-label';
    label.textContent = 'Project Mode';
    row.appendChild(label);

    const control = createSwitch(false, (checked) => void this.setProjectMode(checked), 'Project Mode');
    this.projectModeInput = control.querySelector('input')!;
    row.appendChild(control);
    section.appendChild(row);

    const hint = document.createElement('div');
    hint.className = 'project-mode-hint';
    hint.textContent = 'Turns on the roadmap / context-tree system for agent-mode sessions.';
    section.appendChild(hint);

    return section;
  }

  private async setProjectMode(enabled: boolean): Promise<void> {
    const settings = await getContextTreesSettings();
    await saveContextTreesSettings({ ...settings, enabled });
    this.projectModeInput.checked = enabled;
  }

  private renderFooter() {
    this.footerRoot.innerHTML = '';
    this.footerBodyEl = document.createElement('div');
    this.footerBodyEl.className = 'rail-account';
    this.footerRoot.appendChild(this.footerBodyEl);
    this.footerRoot.appendChild(createThemeToggle());
  }

  private select(item: NavItemSpec) {
    this.setActive(item.id);
    if (item.id === 'account' && !isNarrow()) {
      this.callbacks.onOpenAccount();
      return;
    }
    setView(item.view);
    if (isNarrow() || !item.target) return;
    const target = document.querySelector<HTMLElement>(item.target);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    target.classList.add('nav-flash');
    setTimeout(() => target.classList.remove('nav-flash'), 900);
  }

  private setActive(id: NavItemId) {
    this.active = id;
    for (const [itemId, button] of this.buttons) {
      const on = itemId === id;
      button.classList.toggle('active', on);
      button.setAttribute('aria-current', on ? 'page' : 'false');
    }
  }

  /** Keeps the rail's highlight honest when the bottom tab bar changes the view instead. */
  private syncActiveToView(view: ViewId) {
    const activeSpec = NAV_ITEMS.find((i) => i.id === this.active);
    if (activeSpec?.view === view) return; // pipeline/benchmarks share a view — don't fight over it
    const match = NAV_ITEMS.find((i) => i.view === view);
    if (match) this.setActive(match.id);
  }

  private setBadge(id: NavItemId, text: string, tone: 'plain' | 'good' | 'muted' = 'plain') {
    const badge = this.badges.get(id);
    if (!badge) return;
    badge.textContent = text;
    badge.classList.toggle('empty', text === '');
    badge.classList.toggle('good', tone === 'good');
    badge.classList.toggle('muted', tone === 'muted');
  }

  /** Re-reads every store the rail displays. Cheap enough to call on any state change. */
  async refresh(): Promise<void> {
    const [files, source, settings, contextTrees, benchmarks, apiKey] = await Promise.all([
      getAllFiles(),
      getGitHubSource(),
      getOptimizationSettings(),
      getContextTreesSettings(),
      getCumulativeBenchmarkStats(),
      getApiKey(),
    ]);

    this.projectModeInput.checked = contextTrees.enabled;
    const enabled = PIPELINE_STAGES.filter((stage) => isStageEffectivelyEnabled(settings, stage)).length;
    this.setBadge('pipeline', `${enabled}/${PIPELINE_STAGES.length}`, enabled > 0 ? 'plain' : 'muted');
    this.setBadge('benchmarks', benchmarks.requests ? `${benchmarks.requests}` : '');
    const session = getStoredSession();
    this.setBadge('account', session ? 'synced' : 'local', session ? 'good' : 'muted');

    let dirty = 0;
    if (source) {
      const tracked = files.filter((file) => file.githubSha);
      const hashes = await Promise.all(tracked.map((file) => contentHash(file.content)));
      dirty = tracked.filter((file, i) => hashes[i] !== file.githubSyncedHash).length;
    }
    this.renderWorkspace(files.length, source, dirty);
    this.renderFooterBody(session?.email ?? null, Boolean(apiKey));
  }

  private renderWorkspace(fileCount: number, source: { owner: string; repo: string; branch: string } | null, dirty: number) {
    if (fileCount === 0) {
      this.workspaceEl.innerHTML =
        '<div class="workspace-label">Workspace</div><div class="workspace-name muted">No project loaded</div><div class="workspace-hint">Open a folder or connect a repo below.</div>';
      return;
    }
    const name = source ? `${source.owner}/${source.repo}` : 'Local folder';
    const chips = [
      source ? `<span class="chip mono">${escapeHtml(source.branch)}</span>` : '<span class="chip">on this device</span>',
      `<span class="chip mono">${fileCount} files</span>`,
      source && dirty > 0 ? `<span class="chip dirty mono">${dirty} uncommitted</span>` : '',
    ].join('');
    this.workspaceEl.innerHTML = `<div class="workspace-label">Workspace</div><div class="workspace-name" title="${escapeHtml(
      name,
    )}">${escapeHtml(name)}</div><div class="workspace-meta">${chips}</div>`;
  }

  private renderFooterBody(email: string | null, hasApiKey: boolean) {
    const keyLine = hasApiKey ? 'API key stored on this device' : 'No API key yet';
    this.footerBodyEl.innerHTML = `
      <span class="status-dot ${hasApiKey ? 'ok' : 'off'}"></span>
      <span class="rail-account-text">
        <span class="rail-account-primary">${escapeHtml(email ?? 'Local-only mode')}</span>
        <span class="rail-account-secondary">${keyLine}</span>
      </span>`;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
